import { Alert, ExternalLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { ProductParentCard } from '../components/product-parent-card';
import { ProductPricingCard } from '../components/product-pricing-card';
import { ProductStockCard } from '../components/product-stock-card';
import { ProductVariantsCard } from '../components/product-variants-card';
import { DOCS_URLS, I18N_DATA_PAGE, PRODUCTS_SYNC_PATH } from '../constants';
import { useVariantLookup } from '../hooks/use-variant-lookup';

export const ProductDataPage = withPageProviders(() => {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const { variantSku } = navigationHandler.params();

  const { parent, variant, selectedVariantSku, isLoading, isError, refetch, notFound } =
    useVariantLookup(variantSku);

  const handleVariantSelect = (sku: string) => {
    navigationHandler.navigate(`${PRODUCTS_SYNC_PATH}/${encodeURIComponent(sku)}`, {
      replace: true,
    });
  };

  return (
    <Page
      title={t(`${I18N_DATA_PAGE}.title`)}
      description={t(`${I18N_DATA_PAGE}.description`, { sku: variantSku ?? '' })}
      backUrl={PRODUCTS_SYNC_PATH}
    >
      {notFound ? (
        <Alert variant="warning" headingLevel="h2">
          <span className="flex flex-wrap items-center gap-x-1">
            {t(`${I18N_DATA_PAGE}.not_found`, { sku: variantSku ?? '' })}
            <ExternalLink link={DOCS_URLS.troubleshooting} showIcon>
              {t(`${I18N_DATA_PAGE}.troubleshooting_link`)}
            </ExternalLink>
          </span>
        </Alert>
      ) : (
        <SparkSuspense
          isLoading={isLoading}
          isError={isError}
          errorMessage={t('errors:generic')}
          onReload={() => void refetch()}
        >
          {parent && variant && (
            <>
              <ProductParentCard product={parent} />
              <ProductVariantsCard
                product={parent}
                selectedSku={variant.identifiers.sku}
                onSelect={handleVariantSelect}
                platform={env.connected_platform}
              />
              <ProductStockCard variant={variant} />
              <ProductPricingCard sku={selectedVariantSku} />
            </>
          )}
        </SparkSuspense>
      )}
    </Page>
  );
});
